// API 基础配置
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// 通用请求封装
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('API request failed:', error);
    return { success: false, error: 'Network error' };
  }
}

// 结果相关 API
export interface SaveResultInput {
  personalInfo: {
    gender: string;
    ageGroup: string;
    currentStatus: string;
    workField?: string;
  };
  durationSeconds: number;
  result: {
    modes: { natural: string; work: string; stress: string };
    dimensions: Array<{
      dimensionId: string;
      stability: string;
      natural: { resultLetter: string; poleACount: number; poleBCount: number; isWeak: boolean };
      work: { resultLetter: string; poleACount: number; poleBCount: number; isWeak: boolean };
      stress: { resultLetter: string; poleACount: number; poleBCount: number; isWeak: boolean };
    }>;
    overallStability: {
      type: string;
      stableAxes: string[];
      flexibleAxes: Array<{ dimension: string; pattern: string }>;
    };
    crossValidation: {
      rate: number;
      reliable: string;
      totalChecks: number;
      consistentChecks: number;
    };
  };
}

export interface SaveResultResponse {
  id: string;
  shareToken: string;
  shareUrl: string;
}

// 保存测试结果
export async function saveResult(input: SaveResultInput): Promise<{
  success: boolean;
  data?: SaveResultResponse;
  error?: string;
}> {
  return request<SaveResultResponse>('/api/v1/results', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// 获取结果（通过ID）
export async function getResultById(id: string): Promise<{
  success: boolean;
  data?: {
    id: string;
    createdAt: string;
    modes: { natural: string; work: string; stress: string };
    result: SaveResultInput['result'];
    isPublic: boolean;
    shareToken: string;
  };
  error?: string;
}> {
  return request(`/api/v1/results/${id}`);
}

// 获取分享结果（通过分享token）
export async function getSharedResult(token: string): Promise<{
  success: boolean;
  data?: {
    id: string;
    createdAt: string;
    modes: { natural: string; work: string; stress: string };
    result: SaveResultInput['result'];
  };
  error?: string;
}> {
  return request(`/api/v1/results/share/${token}`);
}

// 设置结果公开状态
export async function setResultPublic(id: string, isPublic: boolean): Promise<{
  success: boolean;
  error?: string;
}> {
  return request(`/api/v1/results/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ isPublic }),
  });
}

// 统计相关 API
export interface TypeStats {
  total: number;
  distribution: {
    natural: Record<string, number>;
    work: Record<string, number>;
    stress: Record<string, number>;
  };
}

export async function getTypeStats(): Promise<{
  success: boolean;
  data?: TypeStats;
  error?: string;
}> {
  return request<TypeStats>('/api/v1/stats/types');
}

export async function getDimensionStats(): Promise<{
  success: boolean;
  data?: Record<string, Record<string, Record<string, number>>>;
  error?: string;
}> {
  return request('/api/v1/stats/dimensions');
}

// 反馈相关
export interface FeedbackInput {
  resultId: string;
  overallAccuracy: 'very_match' | 'mostly_match' | 'partly_match' | 'not_quite' | 'not_at_all';
  accurateDimensions?: string[];
  inaccurateDimensions?: string[];
  clarity?: 'clear' | 'mostly_clear' | 'some_unclear';
  coverage?: 'good_coverage' | 'few_mismatch' | 'many_mismatch';
  comments?: string;
}

export async function submitFeedback(input: FeedbackInput): Promise<{
  success: boolean;
  data?: { id: string };
  error?: string;
}> {
  return request('/api/v1/feedback', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
